import { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { CURRENCIES, formatMoney } from "@/lib/currency";
import { invoiceSchema } from "@/lib/schemas";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import { Field, SummaryBar, DataTable } from "@/pages/FridgeLogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, Download, Plus as PlusSm, X, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const plus = (days) => {
  const d = new Date(); d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const emptyForm = () => ({
  invoice_number: `INV-${Date.now().toString().slice(-6)}`,
  client_name: "",
  client_email: "",
  line_items: [{ description: "", quantity: 1, unit_price: 0 }],
  tax_rate: 0,
  currency: "GBP",
  status: "draft",
  issue_date: today(),
  due_date: plus(14),
  notes: "",
});

// Inline field-level error (matches the Signup form pattern).
function FieldError({ children, testId }) {
  if (!children) return null;
  return (
    <div data-testid={testId} className="mt-1 text-[11px] text-[#d94f3d]">{children}</div>
  );
}

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [initialValues, setInitialValues] = useState(emptyForm());

  const load = async () => setItems((await api.get("/invoices")).data);
  useEffect(() => { load(); }, []);

  // Stripe payment polling on return from Checkout
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cancel = params.get("cancel");
    if (cancel) {
      toast.message("Payment cancelled — invoice unchanged");
      const url = new URL(window.location.href);
      url.search = "";
      window.history.replaceState({}, "", url.toString());
      return;
    }
    if (!sessionId) return;
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success(`Invoice #${data.invoice_number} paid 🎉 Receipt sent.`);
          await load();
          const url = new URL(window.location.href);
          url.search = "";
          window.history.replaceState({}, "", url.toString());
          return;
        }
        if (data.status === "expired") {
          toast.error("Stripe session expired. Please retry.");
          return;
        }
        attempts += 1;
        if (attempts >= max) {
          toast.message("Payment processing — refresh in a moment to verify.");
          return;
        }
        setTimeout(poll, 1800);
      } catch (e) {
        console.warn("Invoice payment polling error (will retry):", e);
      }
    };
    toast.message("Verifying payment with Stripe…");
    poll();
  }, []);

  const startPayment = async (inv) => {
    setPayingId(inv.id);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/checkout-session`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't start payment");
      setPayingId(null);
    }
  };

  const openCreate = () => { setEditingId(null); setInitialValues(emptyForm()); setOpen(true); };
  const openEdit = (inv) => {
    setEditingId(inv.id);
    setInitialValues({
      invoice_number: inv.invoice_number,
      client_name: inv.client_name,
      client_email: inv.client_email || "",
      line_items: inv.line_items.length ? inv.line_items.map((li) => ({ ...li })) : [{ description: "", quantity: 1, unit_price: 0 }],
      tax_rate: inv.tax_rate,
      currency: inv.currency || "GBP",
      status: inv.status,
      issue_date: inv.issue_date,
      due_date: inv.due_date,
      notes: inv.notes || "",
    });
    setOpen(true);
  };

  // Called by InvoiceForm after zod has validated + coerced numeric fields.
  const handleFormSubmit = async (values) => {
    try {
      if (editingId) {
        await api.put(`/invoices/${editingId}`, values);
        toast.success("Invoice updated");
      } else {
        await api.post("/invoices", values);
        toast.success("Invoice created");
      }
      setOpen(false);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
      throw err; // let InvoiceForm keep isSubmitting=false correctly
    }
  };

  const del = async (id) => { await api.delete(`/invoices/${id}`); toast.success("Invoice deleted"); load(); };

  const setStatus = async (inv, status) => {
    await api.put(`/invoices/${inv.id}`, {
      invoice_number: inv.invoice_number,
      client_name: inv.client_name,
      client_email: inv.client_email,
      line_items: inv.line_items,
      tax_rate: inv.tax_rate,
      status,
      issue_date: inv.issue_date,
      due_date: inv.due_date,
      notes: inv.notes,
    });
    load();
  };

  const downloadPdf = async (inv) => {
    const res = await fetch(`${API_BASE}/invoices/${inv.id}/pdf`, { credentials: "include" });
    if (!res.ok) { toast.error("Could not generate PDF"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoice-${inv.invoice_number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = items.reduce(
    (acc, i) => {
      const cur = i.currency || "GBP";
      acc.byCur[cur] = (acc.byCur[cur] || { total: 0, paid: 0, overdue: 0 });
      acc.byCur[cur].total += i.total;
      if (i.status === "paid") acc.byCur[cur].paid += i.total;
      if (i.status === "overdue") acc.byCur[cur].overdue += i.total;
      return acc;
    },
    { byCur: {} }
  );

  const currencyOrder = Object.keys(totals.byCur).sort((a, b) => totals.byCur[b].total - totals.byCur[a].total);
  const mainCur = currencyOrder[0] || "GBP";
  const mainTotals = totals.byCur[mainCur] || { total: 0, paid: 0, overdue: 0 };
  const otherCurrencies = currencyOrder.slice(1);

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]" data-testid="invoices-page">
      <PageHeader
        eyebrow="Money in flight"
        title="Invoices"
        description="Issue, track, and chase payments — wholesale, catering, retail. Export PDFs in one click."
        actions={
          <Button data-testid="invoice-add-button" onClick={openCreate} className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] h-10 px-5">
            <Plus className="w-4 h-4 mr-2" /> New invoice
          </Button>
        }
      />

      <SummaryBar items={[
        { label: `Invoiced (${mainCur})`, value: formatMoney(mainTotals.total, mainCur), testid: "stat-total-invoiced" },
        { label: "Paid", value: formatMoney(mainTotals.paid, mainCur), testid: "stat-paid" },
        { label: "Overdue", value: formatMoney(mainTotals.overdue, mainCur), tone: mainTotals.overdue ? "alert" : "success", testid: "stat-overdue" },
      ]} />

      {otherCurrencies.length > 0 && (
        <div className="mb-6 -mt-3 text-xs text-[#8a8476]" data-testid="stat-other-currencies">
          Other currencies in flight:{" "}
          {otherCurrencies.map((c, i) => (
            <span key={c} className="font-mono">
              {i > 0 && ", "}
              {formatMoney(totals.byCur[c].total, c)}
            </span>
          ))}
        </div>
      )}

      <DataTable
        empty="No invoices yet. Create your first invoice."
        head={["Number", "Client", "Issue", "Due", "Total", "Status", ""]}
        rows={items}
        testid="invoices-table"
        render={(i, idx) => (
          <tr key={i.id} data-testid={`invoice-row-${idx}`} className="border-t border-[#e5e2d9] hover:bg-[#efece6]/40">
            <td className="px-4 py-3 text-sm font-mono">{i.invoice_number}</td>
            <td className="px-4 py-3 text-sm">
              <div>{i.client_name}</div>
              <div className="text-xs text-[#8a8476]">{i.client_email}</div>
            </td>
            <td className="px-4 py-3 text-sm whitespace-nowrap">{i.issue_date}</td>
            <td className="px-4 py-3 text-sm whitespace-nowrap">{i.due_date}</td>
            <td className="px-4 py-3 text-sm font-mono">{formatMoney(i.total, i.currency || "GBP")}</td>
            <td className="px-4 py-3">
              <Select value={i.status} onValueChange={(v) => setStatus(i, v)}>
                <SelectTrigger data-testid={`invoice-status-${idx}`} className="h-8 w-[120px] rounded-full px-3 text-xs border-0 bg-transparent">
                  <StatusPill tone={i.status === "paid" ? "success" : i.status === "overdue" ? "alert" : i.status === "sent" ? "warning" : "neutral"}>{i.status}</StatusPill>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">draft</SelectItem>
                  <SelectItem value="sent">sent</SelectItem>
                  <SelectItem value="paid">paid</SelectItem>
                  <SelectItem value="overdue">overdue</SelectItem>
                </SelectContent>
              </Select>
            </td>
            <td className="px-4 py-3 text-right">
              <div className="inline-flex items-center gap-3">
                {i.status !== "paid" && (i.total || 0) > 0 && (
                  <button
                    data-testid={`invoice-pay-${idx}`}
                    onClick={() => startPayment(i)}
                    disabled={payingId === i.id}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-white bg-[#1b3b36] hover:bg-[#2c5a53] rounded-[3px] disabled:opacity-60"
                    title="Pay via Stripe"
                  >
                    {payingId === i.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}
                    {payingId === i.id ? "Opening…" : "Pay"}
                  </button>
                )}
                <button data-testid={`invoice-edit-${idx}`} onClick={() => openEdit(i)} className="text-[#8a8476] hover:text-[#1b3b36]" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`invoice-pdf-${idx}`} onClick={() => downloadPdf(i)} className="text-[#1b3b36] hover:text-[#2c5a53]" aria-label="PDF"><Download className="w-4 h-4" /></button>
                <button data-testid={`invoice-delete-${idx}`} onClick={() => del(i.id)} className="text-[#8a8476] hover:text-[#d94f3d]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </td>
          </tr>
        )}
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editingId ? "Edit invoice" : "New invoice"}</DialogTitle></DialogHeader>
          <InvoiceForm
            key={editingId || "create"}
            defaultValues={initialValues}
            editing={!!editingId}
            onSubmit={handleFormSubmit}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// InvoiceForm — react-hook-form + zod. Catches negative quantities, missing
// client name, dates out of order, empty line items BEFORE round-trip.
// ---------------------------------------------------------------------------
function InvoiceForm({ defaultValues, editing, onSubmit }) {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(invoiceSchema),
    defaultValues,
    mode: "onTouched",
  });

  const { fields, append, remove } = useFieldArray({ control, name: "line_items" });

  const submit = async (values) => {
    try { await onSubmit(values); } catch { /* parent toasted; keep dialog open */ }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit(submit)} data-testid="invoice-form" noValidate>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Invoice #">
          <Input data-testid="invoice-number-input" className="bg-white rounded-[4px] font-mono" aria-invalid={!!errors.invoice_number} {...register("invoice_number")} />
          <FieldError testId="invoice-number-error">{errors.invoice_number?.message}</FieldError>
        </Field>
        <Field label="Status">
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger data-testid="invoice-status-select" className="bg-white rounded-[4px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Client">
          <Input data-testid="invoice-client-input" className="bg-white rounded-[4px]" aria-invalid={!!errors.client_name} {...register("client_name")} />
          <FieldError testId="invoice-client-error">{errors.client_name?.message}</FieldError>
        </Field>
        <Field label="Client email">
          <Input type="email" data-testid="invoice-email-input" className="bg-white rounded-[4px]" aria-invalid={!!errors.client_email} {...register("client_email")} />
          <FieldError testId="invoice-email-error">{errors.client_email?.message}</FieldError>
        </Field>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <Field label="Issue date">
          <Input type="date" data-testid="invoice-issue-input" className="bg-white rounded-[4px]" aria-invalid={!!errors.issue_date} {...register("issue_date")} />
          <FieldError testId="invoice-issue-error">{errors.issue_date?.message}</FieldError>
        </Field>
        <Field label="Due date">
          <Input type="date" data-testid="invoice-due-input" className="bg-white rounded-[4px]" aria-invalid={!!errors.due_date} {...register("due_date")} />
          <FieldError testId="invoice-due-error">{errors.due_date?.message}</FieldError>
        </Field>
        <Field label="Currency">
          <Controller
            control={control}
            name="currency"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger data-testid="invoice-currency-select" className="bg-white rounded-[4px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`invoice-currency-${c.code}`}>
                      <span className="font-mono mr-2">{c.symbol.trim()}</span>{c.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>
        <Field label="Tax %">
          <Input type="number" step="0.1" data-testid="invoice-tax-input" className="bg-white rounded-[4px]" aria-invalid={!!errors.tax_rate} {...register("tax_rate")} />
          <FieldError testId="invoice-tax-error">{errors.tax_rate?.message}</FieldError>
        </Field>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476] mb-2">Line items</div>
        <div className="space-y-2">
          {fields.map((f, idx) => (
            <div key={f.id} className="grid grid-cols-12 gap-2 items-start" data-testid={`invoice-line-${idx}`}>
              <div className="col-span-6">
                <Input placeholder="Description" data-testid={`invoice-line-${idx}-description`} className="bg-white rounded-[4px]" aria-invalid={!!errors.line_items?.[idx]?.description} {...register(`line_items.${idx}.description`)} />
                <FieldError testId={`invoice-line-${idx}-description-error`}>{errors.line_items?.[idx]?.description?.message}</FieldError>
              </div>
              <div className="col-span-2">
                <Input type="number" step="any" placeholder="Qty" data-testid={`invoice-line-${idx}-qty`} className="bg-white rounded-[4px] font-mono" aria-invalid={!!errors.line_items?.[idx]?.quantity} {...register(`line_items.${idx}.quantity`)} />
                <FieldError testId={`invoice-line-${idx}-qty-error`}>{errors.line_items?.[idx]?.quantity?.message}</FieldError>
              </div>
              <div className="col-span-3">
                <Input type="number" step="0.01" placeholder="Price" data-testid={`invoice-line-${idx}-price`} className="bg-white rounded-[4px] font-mono" aria-invalid={!!errors.line_items?.[idx]?.unit_price} {...register(`line_items.${idx}.unit_price`)} />
                <FieldError testId={`invoice-line-${idx}-price-error`}>{errors.line_items?.[idx]?.unit_price?.message}</FieldError>
              </div>
              <button type="button" data-testid={`invoice-line-${idx}-remove`} onClick={() => remove(idx)} className="col-span-1 text-[#8a8476] hover:text-[#d94f3d] mt-2" aria-label="Remove line">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        {errors.line_items?.message && <FieldError testId="invoice-line-items-error">{errors.line_items.message}</FieldError>}
        {errors.line_items?.root?.message && <FieldError testId="invoice-line-items-error">{errors.line_items.root.message}</FieldError>}
        <button type="button" data-testid="invoice-add-line" onClick={() => append({ description: "", quantity: 1, unit_price: 0 })} className="mt-2 text-xs text-[#1b3b36] hover:underline inline-flex items-center gap-1">
          <PlusSm className="w-3 h-3" /> Add line
        </button>
      </div>

      <Field label="Notes">
        <Textarea className="bg-white rounded-[4px]" {...register("notes")} />
        <FieldError testId="invoice-notes-error">{errors.notes?.message}</FieldError>
      </Field>

      <DialogFooter>
        <Button type="submit" disabled={isSubmitting} data-testid="invoice-submit-button" className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]">
          {isSubmitting ? "Saving…" : editing ? "Save changes" : "Save invoice"}
        </Button>
      </DialogFooter>
    </form>
  );
}
